import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ForumThread, ForumPost, Comment } from '../types'
import CommentSystem from '../components/CommentSystem'
import VoteSystem from '../components/VoteSystem'
import ReactionSystem, { Reaction } from '../components/ReactionSystem'
import ShareSystem from '../components/ShareSystem'

// Mock thread data - replace with API call
const mockThread: ForumThread = {
  id: 'thread1',
  categoryId: 'general',
  title: 'Welcome to the community!',
  content: 'Hey everyone! Welcome to our kratom community forum. This is a place where we can share experiences, ask questions, and help each other out. Looking forward to great discussions!',
  authorId: 'user1',
  authorUsername: 'KratomExpert',
  authorRole: 'trusted',
  dateCreated: new Date('2024-12-08T10:30:00Z'),
  lastActivity: new Date('2024-12-08T14:30:00Z'),
  isPinned: true,
  isLocked: false,
  isDeleted: false,
  views: 1247,
  postCount: 23,
  upvotes: 45,
  downvotes: 2,
  tags: ['welcome', 'community', 'introduction']
}

// Mock posts data - replace with API call
const mockPosts: ForumPost[] = [
  {
    id: 'post1',
    threadId: 'thread1',
    authorId: 'user2',
    authorUsername: 'NewMember',
    authorRole: 'member',
    content: 'Thanks for the warm welcome! I\'m new to kratom and excited to learn from everyone here.',
    dateCreated: new Date('2024-12-08T11:00:00Z'),
    isDeleted: false,
    upvotes: 12,
    downvotes: 0,
    mentions: [],
    attachments: []
  },
  {
    id: 'post2',
    threadId: 'thread1',
    authorId: 'vendor1',
    authorUsername: 'QualityVendor',
    authorRole: 'verified_vendor',
    content: 'Welcome to the community! If you have any questions about different strains or quality, feel free to ask. Always happy to help educate newcomers.',
    dateCreated: new Date('2024-12-08T12:15:00Z'),
    isDeleted: false,
    upvotes: 18,
    downvotes: 1,
    mentions: [],
    attachments: []
  }
]

// Mock reactions data - replace with API call
const mockReactions: { [key: string]: Reaction[] } = {
  'thread1': [
    {
      id: 'reaction1',
      userId: 'user2',
      username: 'NewMember',
      type: 'like',
      timestamp: new Date('2024-12-08T11:30:00Z')
    },
    {
      id: 'reaction2',
      userId: 'user3',
      username: 'HelpfulUser',
      type: 'love',
      timestamp: new Date('2024-12-08T12:00:00Z')
    }
  ],
  'post1': [
    {
      id: 'reaction3',
      userId: 'vendor1',
      username: 'QualityVendor',
      type: 'like',
      timestamp: new Date('2024-12-08T11:45:00Z')
    }
  ]
}

// Mock comments data - replace with API call
const mockComments: Comment[] = [
  {
    id: 'comment1',
    postId: 'post1',
    authorId: 'user3',
    authorUsername: 'HelpfulUser',
    authorRole: 'trusted',
    content: 'Great question! I\'d be happy to help. What specific aspects of kratom are you most curious about?',
    dateCreated: new Date('2024-12-08T11:30:00Z'),
    isDeleted: false,
    likes: 8,
    mentions: ['user2']
  },
  {
    id: 'comment2',
    postId: 'post1',
    authorId: 'user2',
    authorUsername: 'NewMember',
    authorRole: 'member',
    content: '@HelpfulUser Thanks! I\'m mostly interested in understanding the different strains and their effects.',
    dateCreated: new Date('2024-12-08T11:45:00Z'),
    isDeleted: false,
    likes: 3,
    replyToCommentId: 'comment1',
    mentions: ['user3']
  },
  {
    id: 'comment3',
    postId: 'post2',
    authorId: 'user4',
    authorUsername: 'StrainExpert',
    authorRole: 'member',
    content: 'This vendor really knows their stuff! I\'ve been ordering from them for months.',
    dateCreated: new Date('2024-12-08T12:30:00Z'),
    isDeleted: false,
    likes: 15,
    mentions: []
  },
  {
    id: 'comment4',
    postId: 'post1',
    authorId: 'user3',
    authorUsername: 'HelpfulUser',
    authorRole: 'trusted',
    content: '@NewMember Perfect! Red strains are typically more relaxing, greens are balanced, and whites are more energizing. Each person responds differently though!',
    dateCreated: new Date('2024-12-08T12:00:00Z'),
    isDeleted: false,
    likes: 12,
    replyToCommentId: 'comment2',
    mentions: ['user2']
  }
]

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#4299e1'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '⭐'
    default: return ''
  }
}

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

export default function ThreadView() {
  const { threadId } = useParams<{ threadId: string }>()
  const navigate = useNavigate()
  const { user, isLoggedIn } = useAuth()
  const [thread, setThread] = useState<ForumThread | null>(null)
  const [posts, setPosts] = useState<ForumPost[]>([])
  const [comments, setComments] = useState<{ [postId: string]: Comment[] }>({})
  const [reactions, setReactions] = useState<{ [targetId: string]: Reaction[] }>({})
  const [userVotes, setUserVotes] = useState<{ [targetId: string]: 'upvote' | 'downvote' }>({})
  const [loading, setLoading] = useState(true)
  const [newPostContent, setNewPostContent] = useState('')
  const [isSubmittingPost, setIsSubmittingPost] = useState(false)

  useEffect(() => {
    const loadThread = async () => {
      if (!threadId) return
      
      setLoading(true)
      try {
        // TODO: Replace with actual API calls
        setThread(mockThread)
        setPosts(mockPosts)
        
        // Group comments by post ID
        const commentsByPost: { [postId: string]: Comment[] } = {}
        mockComments.forEach(comment => {
          if (!commentsByPost[comment.postId]) {
            commentsByPost[comment.postId] = []
          }
          commentsByPost[comment.postId].push(comment)
        })
        setComments(commentsByPost)
        
        // Load reactions
        setReactions(mockReactions)
        
        // TODO: Load user's existing votes
        // setUserVotes(existingUserVotes)
        
        // Increment view count
        // TODO: API call to increment views
        
      } catch (error) {
        console.error('Error loading thread:', error)
      } finally {
        setLoading(false)
      }
    }

    loadThread()
  }, [threadId])

  const handleVote = async (type: 'upvote' | 'downvote', postId?: string) => {
    if (!isLoggedIn) {
      // TODO: Show login prompt
      return
    }

    // TODO: Implement voting logic
    console.log(`${type} on ${postId ? `post ${postId}` : 'thread'}`)
  }

  const handleAddComment = async (postId: string, content: string, replyToCommentId?: string) => {
    if (!isLoggedIn || !user) return

    const newComment: Comment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      postId,
      authorId: user.id,
      authorUsername: user.username,
      authorRole: user.role,
      content,
      dateCreated: new Date(),
      isDeleted: false,
      likes: 0,
      replyToCommentId,
      mentions: content.match(/@(\w+)/g) || []
    }

    // TODO: API call to create comment
    console.log('Creating comment:', newComment)

    // Add to local state
    setComments(prev => ({
      ...prev,
      [postId]: [...(prev[postId] || []), newComment]
    }))
  }

  const handleVoteComment = async (commentId: string, type: 'upvote' | 'downvote') => {
    if (!isLoggedIn) return

    // TODO: Implement comment voting
    console.log(`${type} on comment ${commentId}`)

    // Update local state
    setComments(prev => {
      const newComments = { ...prev }
      Object.keys(newComments).forEach(postId => {
        newComments[postId] = newComments[postId].map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              likes: type === 'upvote' ? comment.likes + 1 : comment.likes - 1
            }
          }
          return comment
        })
      })
      return newComments
    })
  }

  const handleEditComment = async (commentId: string, newContent: string) => {
    // TODO: API call to edit comment
    console.log(`Editing comment ${commentId}:`, newContent)

    // Update local state
    setComments(prev => {
      const newComments = { ...prev }
      Object.keys(newComments).forEach(postId => {
        newComments[postId] = newComments[postId].map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              content: newContent,
              dateEdited: new Date()
            }
          }
          return comment
        })
      })
      return newComments
    })
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return

    // TODO: API call to delete comment
    console.log(`Deleting comment ${commentId}`)

    // Update local state
    setComments(prev => {
      const newComments = { ...prev }
      Object.keys(newComments).forEach(postId => {
        newComments[postId] = newComments[postId].filter(comment => comment.id !== commentId)
      })
      return newComments
    })
  }

  const handleReaction = async (targetId: string, reactionType: Reaction['type']) => {
    if (!isLoggedIn || !user) return

    const newReaction: Reaction = {
      id: `reaction_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: user.id,
      username: user.username,
      type: reactionType,
      timestamp: new Date()
    }

    // TODO: API call to add reaction
    console.log('Adding reaction:', newReaction)

    // Update local state
    setReactions(prev => {
      const existingReactions = prev[targetId] || []
      const userReactionIndex = existingReactions.findIndex(r => r.userId === user.id)
      
      if (userReactionIndex >= 0) {
        // Replace existing reaction
        const newReactions = [...existingReactions]
        newReactions[userReactionIndex] = newReaction
        return { ...prev, [targetId]: newReactions }
      } else {
        // Add new reaction
        return { ...prev, [targetId]: [...existingReactions, newReaction] }
      }
    })
  }

  const handleRemoveReaction = async (targetId: string) => {
    if (!isLoggedIn || !user) return

    // TODO: API call to remove reaction
    console.log('Removing reaction for target:', targetId)

    // Update local state
    setReactions(prev => ({
      ...prev,
      [targetId]: (prev[targetId] || []).filter(r => r.userId !== user.id)
    }))
  }

  const handleVoteUpdate = async (targetId: string, voteType: 'upvote' | 'downvote') => {
    if (!isLoggedIn || !user) return

    // TODO: API call to update vote
    console.log(`Voting ${voteType} on ${targetId}`)

    // Update local state
    setUserVotes(prev => ({ ...prev, [targetId]: voteType }))

    // Update thread/post vote counts
    if (targetId === thread?.id) {
      setThread(prev => {
        if (!prev) return prev
        const currentVote = userVotes[targetId]
        let upvotes = prev.upvotes
        let downvotes = prev.downvotes

        // Remove previous vote
        if (currentVote === 'upvote') upvotes--
        if (currentVote === 'downvote') downvotes--

        // Add new vote
        if (voteType === 'upvote') upvotes++
        if (voteType === 'downvote') downvotes++

        return { ...prev, upvotes, downvotes }
      })
    } else {
      // Update post votes
      setPosts(prev => prev.map(post => {
        if (post.id === targetId) {
          const currentVote = userVotes[targetId]
          let upvotes = post.upvotes
          let downvotes = post.downvotes

          // Remove previous vote
          if (currentVote === 'upvote') upvotes--
          if (currentVote === 'downvote') downvotes--

          // Add new vote
          if (voteType === 'upvote') upvotes++
          if (voteType === 'downvote') downvotes++

          return { ...post, upvotes, downvotes }
        }
        return post
      }))
    }
  }

  const handleRemoveVote = async (targetId: string) => {
    if (!isLoggedIn || !user) return

    // TODO: API call to remove vote
    console.log('Removing vote for target:', targetId)

    const currentVote = userVotes[targetId]
    if (!currentVote) return

    // Update local state
    setUserVotes(prev => {
      const newVotes = { ...prev }
      delete newVotes[targetId]
      return newVotes
    })

    // Update thread/post vote counts
    if (targetId === thread?.id) {
      setThread(prev => {
        if (!prev) return prev
        let upvotes = prev.upvotes
        let downvotes = prev.downvotes

        if (currentVote === 'upvote') upvotes--
        if (currentVote === 'downvote') downvotes--

        return { ...prev, upvotes, downvotes }
      })
    } else {
      setPosts(prev => prev.map(post => {
        if (post.id === targetId) {
          let upvotes = post.upvotes
          let downvotes = post.downvotes

          if (currentVote === 'upvote') upvotes--
          if (currentVote === 'downvote') downvotes--

          return { ...post, upvotes, downvotes }
        }
        return post
      }))
    }
  }

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoggedIn || !newPostContent.trim() || !user) return

    setIsSubmittingPost(true)
    try {
      const newPost: ForumPost = {
        id: `post_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        threadId: threadId!,
        authorId: user.id,
        authorUsername: user.username,
        authorRole: user.role,
        content: newPostContent.trim(),
        dateCreated: new Date(),
        isDeleted: false,
        upvotes: 0,
        downvotes: 0,
        mentions: [],
        attachments: []
      }

      // TODO: API call to create post
      console.log('Creating post:', newPost)
      
      // Add to local state
      setPosts(prev => [...prev, newPost])
      setNewPostContent('')
      
      // Update thread post count
      if (thread) {
        setThread(prev => prev ? { ...prev, postCount: prev.postCount + 1 } : null)
      }
      
    } catch (error) {
      console.error('Error creating post:', error)
    } finally {
      setIsSubmittingPost(false)
    }
  }

  if (loading) {
    return (
      <div className="thread-view-page">
        <div className="container">
          <div className="loading">Loading thread...</div>
        </div>
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="thread-view-page">
        <div className="container">
          <div className="not-found">
            <h1>Thread Not Found</h1>
            <p>The thread you're looking for doesn't exist or has been deleted.</p>
            <Link to="/community" className="btn-primary">Back to Community</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="thread-view-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/community">Community</Link>
          <span>→</span>
          <Link to={`/community/category/${thread.categoryId}`}>
            {thread.categoryId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Link>
          <span>→</span>
          <span>{thread.title}</span>
        </nav>

        {/* Thread Header */}
        <div className="thread-header">
          <div className="thread-title-section">
            <h1 className="thread-title">
              {thread.isPinned && <span className="pinned-badge">📌</span>}
              {thread.isLocked && <span className="locked-badge">🔒</span>}
              {thread.title}
            </h1>
            
            {thread.tags.length > 0 && (
              <div className="thread-tags">
                {thread.tags.map(tag => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
            )}
          </div>

          <div className="thread-stats">
            <span className="stat">{thread.views.toLocaleString()} views</span>
            <span className="stat">{thread.postCount} replies</span>
            <span className="stat">Last activity {formatTimestamp(thread.lastActivity)}</span>
          </div>
        </div>

        {/* Original Post */}
        <div className="original-post">
          <div className="post-sidebar">
            <div className="author-info">
              <Link to={`/profile/${thread.authorId}`} className="author-avatar">
                <div className="avatar-placeholder">{thread.authorUsername[0].toUpperCase()}</div>
              </Link>
              <div className="author-details">
                <Link 
                  to={`/profile/${thread.authorId}`}
                  className="author-name"
                  style={{ color: getRoleColor(thread.authorRole) }}
                >
                  {getRoleBadge(thread.authorRole)} {thread.authorUsername}
                </Link>
                <span className="post-date">{formatTimestamp(thread.dateCreated)}</span>
              </div>
            </div>
            
            <VoteSystem
              targetId={thread.id}
              targetType="thread"
              initialUpvotes={thread.upvotes}
              initialDownvotes={thread.downvotes}
              userVote={userVotes[thread.id]}
              onVote={(type) => handleVoteUpdate(thread.id, type)}
              onRemoveVote={() => handleRemoveVote(thread.id)}
              orientation="vertical"
              size="medium"
              animated={true}
            />
          </div>

          <div className="post-content">
            <div className="content-text">{thread.content}</div>
            
            <div className="post-interactions">
              <ReactionSystem
                targetId={thread.id}
                targetType="thread"
                reactions={reactions[thread.id] || []}
                onReact={(type) => handleReaction(thread.id, type)}
                onRemoveReaction={() => handleRemoveReaction(thread.id)}
                showLabels={true}
                size="medium"
              />
              
              <ShareSystem
                targetId={thread.id}
                targetType="thread"
                title={thread.title}
                description={thread.content.slice(0, 200)}
                author={thread.authorUsername}
                onShare={(platform) => console.log(`Shared thread to ${platform}`)}
              />
            </div>
          </div>
        </div>

        {/* Posts */}
        <div className="thread-posts">
          <h3>Replies ({posts.length})</h3>
          
          {posts.map((post, index) => (
            <div key={post.id} className="thread-post">
              <div className="post-sidebar">
                <div className="author-info">
                  <Link to={`/profile/${post.authorId}`} className="author-avatar">
                    <div className="avatar-placeholder">{post.authorUsername[0].toUpperCase()}</div>
                  </Link>
                  <div className="author-details">
                    <Link 
                      to={`/profile/${post.authorId}`}
                      className="author-name"
                      style={{ color: getRoleColor(post.authorRole) }}
                    >
                      {getRoleBadge(post.authorRole)} {post.authorUsername}
                    </Link>
                    <span className="post-date">{formatTimestamp(post.dateCreated)}</span>
                  </div>
                </div>
                
                <VoteSystem
                  targetId={post.id}
                  targetType="post"
                  initialUpvotes={post.upvotes}
                  initialDownvotes={post.downvotes}
                  userVote={userVotes[post.id]}
                  onVote={(type) => handleVoteUpdate(post.id, type)}
                  onRemoveVote={() => handleRemoveVote(post.id)}
                  orientation="vertical"
                  size="small"
                  animated={true}
                />
              </div>

              <div className="post-content">
                <div className="post-number">#{index + 1}</div>
                <div className="content-text">{post.content}</div>
                
                <div className="post-interactions">
                  <ReactionSystem
                    targetId={post.id}
                    targetType="post"
                    reactions={reactions[post.id] || []}
                    onReact={(type) => handleReaction(post.id, type)}
                    onRemoveReaction={() => handleRemoveReaction(post.id)}
                    showLabels={false}
                    size="small"
                  />
                  
                  <ShareSystem
                    targetId={post.id}
                    targetType="post"
                    title={`Reply by ${post.authorUsername}`}
                    description={post.content.slice(0, 200)}
                    author={post.authorUsername}
                    onShare={(platform) => console.log(`Shared post to ${platform}`)}
                  />
                </div>
                
                {/* Comments for this post */}
                {(comments[post.id] && comments[post.id].length > 0) || isLoggedIn ? (
                  <div className="post-comments">
                    <CommentSystem
                      postId={post.id}
                      comments={comments[post.id] || []}
                      onAddComment={(content, replyToCommentId) => handleAddComment(post.id, content, replyToCommentId)}
                      onVoteComment={handleVoteComment}
                      onEditComment={handleEditComment}
                      onDeleteComment={handleDeleteComment}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* Reply Form */}
        {isLoggedIn ? (
          <div className="reply-form-section">
            <h3>Reply to Thread</h3>
            <form onSubmit={handleSubmitPost} className="reply-form">
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Share your thoughts..."
                rows={4}
                required
                minLength={5}
              />
              <div className="form-actions">
                <span className="char-count">{newPostContent.length} characters</span>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmittingPost || newPostContent.trim().length < 5}
                >
                  {isSubmittingPost ? 'Posting...' : 'Post Reply'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="login-prompt">
            <p>You must be logged in to reply to this thread.</p>
            <Link to="/login" className="btn-primary">Login</Link>
          </div>
        )}
      </div>
    </div>
  )
}